import { useOutletContext, Link } from 'react-router-dom';
import {
  Bot,
  Plus,
  Activity,
  Mic,
  Calendar,
  LifeBuoy,
  ArrowRight,
  Mail,
  PlayCircle,
  CheckCircle2,
  Circle,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardHeader, Badge, ProgressBar, Button } from '@/components/ui';
import { PACK, AGENTS } from '@/lib/pack';
import { engagementDaysRemaining, ENGAGEMENT_MIN_MONTHS } from '@/lib/engagement';
import type { Tenant } from '@/hooks/useTenant';

const STRIPE_PORTAL_URL =
  import.meta.env.VITE_STRIPE_PORTAL_URL ?? 'https://billing.stripe.com/p/login/test_xxx';
const ONBOARDING_LOOM_URL =
  import.meta.env.VITE_ONBOARDING_LOOM_URL ?? 'https://www.loom.com/share/placeholder';

export function HomeDashboardPage() {
  const { tenant } = useOutletContext<{ tenant: Tenant }>();
  const packInfo = PACK[tenant.pack];
  const activeAgents = tenant.active_agents.length > 0 ? tenant.active_agents : packInfo.defaultAgents;
  const daysRemaining = engagementDaysRemaining(tenant.started_at);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">
          Bonjour {tenant.contact_name?.split(' ')[0] ?? 'coach'} 👋
        </h1>
        <p className="text-sm text-ducens-muted mt-1">
          Voici l'état de ton compte Ducens. Les agents tournent en fond, tu valides quand tu veux.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AgentsCard activeAgents={activeAgents} packName={packInfo.name} />
        <QuotaCard usage={tenant.api_usage_month} max={packInfo.apiQuotaMonthly} />
        <VoiceProfileCard status={tenant.voice_profile_status} />
        <EngagementCard daysRemaining={daysRemaining} startedAt={tenant.started_at} />
      </div>

      <SupportCard />
    </div>
  );
}

function AgentsCard({ activeAgents, packName }: { activeAgents: string[]; packName: string }) {
  return (
    <Card>
      <CardHeader
        title="Mes agents activés"
        description={`Inclus dans le Pack ${packName}.`}
        action={<Bot className="w-5 h-5 text-ducens-muted" aria-hidden="true" />}
      />
      <ul className="space-y-2 mb-5">
        {activeAgents.map((id) => {
          const meta = AGENTS[id];
          return (
            <li
              key={id}
              className="flex items-start justify-between gap-3 p-3 bg-ducens-bg rounded-lg"
            >
              <div className="min-w-0">
                <Link
                  to={`/agents/${id}`}
                  className="font-medium text-sm hover:underline truncate block"
                >
                  {meta?.name ?? id}
                </Link>
                <p className="text-xs text-ducens-muted mt-0.5 truncate">
                  {meta?.description ?? '—'}
                </p>
              </div>
              <Badge variant="success" className="shrink-0">Actif</Badge>
            </li>
          );
        })}
      </ul>
      <Button
        as="link"
        to={STRIPE_PORTAL_URL}
        external
        variant="outline"
        size="sm"
        className="w-full"
      >
        <Plus className="w-4 h-4" />
        Activer un agent supplémentaire (+99 €/mois)
      </Button>
    </Card>
  );
}

function QuotaCard({ usage, max }: { usage: number; max: number }) {
  return (
    <Card>
      <CardHeader
        title="Quota API ce mois"
        description="Reset le 1er de chaque mois."
        action={<Activity className="w-5 h-5 text-ducens-muted" aria-hidden="true" />}
      />
      <ProgressBar value={usage} max={max} label="Exécutions" />
      <p className="text-xs text-ducens-muted mt-4">
        Une exécution = un appel agent (post généré, message envoyé, etc.).
        Si tu dépasses, on te propose un boost +5000 exec à 29 €/mois.
      </p>
    </Card>
  );
}

const VOICE_LABEL: Record<Tenant['voice_profile_status'], { label: string; variant: 'warn' | 'success' | 'neutral'; Icon: typeof Circle }> = {
  empty: { label: 'À remplir', variant: 'warn', Icon: AlertTriangle },
  in_progress: { label: 'En cours', variant: 'neutral', Icon: Circle },
  complete: { label: 'Complet', variant: 'success', Icon: CheckCircle2 },
};

function VoiceProfileCard({ status }: { status: Tenant['voice_profile_status'] }) {
  const meta = VOICE_LABEL[status];
  const StatusIcon = meta.Icon;

  return (
    <Card>
      <CardHeader
        title="Voice Profile"
        description="Le ton, les sujets, les exemples — pour que tes agents écrivent comme toi."
        action={<Mic className="w-5 h-5 text-ducens-muted" aria-hidden="true" />}
      />
      <div className="flex items-center gap-2 mb-5">
        <StatusIcon className="w-4 h-4 text-ducens-muted" aria-hidden="true" />
        <Badge variant={meta.variant}>{meta.label}</Badge>
      </div>
      <Button as="link" to="/voice-profile" variant="primary" size="sm" className="w-full">
        {status === 'complete' ? 'Modifier mon voice profile' : 'Remplir mon voice profile'}
        <ArrowRight className="w-4 h-4" />
      </Button>
    </Card>
  );
}

function EngagementCard({
  daysRemaining,
  startedAt,
}: {
  daysRemaining: number | null;
  startedAt: string | null;
}) {
  return (
    <Card>
      <CardHeader
        title="Engagement"
        description={`Minimum ${ENGAGEMENT_MIN_MONTHS} mois — comme convenu.`}
        action={<Calendar className="w-5 h-5 text-ducens-muted" aria-hidden="true" />}
      />
      {daysRemaining === null ? (
        <p className="text-sm text-ducens-muted">Date de démarrage non renseignée.</p>
      ) : daysRemaining > 0 ? (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-semibold tabular-nums">{daysRemaining}</span>
            <span className="text-sm text-ducens-muted">
              jour{daysRemaining > 1 ? 's' : ''} restants
            </span>
          </div>
          <p className="text-xs text-ducens-muted mt-2">
            Démarré le{' '}
            {startedAt
              ? new Date(startedAt).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              : '—'}
            .
          </p>
        </>
      ) : (
        <>
          <Badge variant="success">Engagement terminé</Badge>
          <p className="text-xs text-ducens-muted mt-2">
            Tu es libre de résilier à tout moment depuis Stripe.
          </p>
        </>
      )}
    </Card>
  );
}

function SupportCard() {
  return (
    <Card>
      <CardHeader
        title="Support"
        description="Une question, un blocage ? Sebastian répond sous 24h ouvrées."
        action={<LifeBuoy className="w-5 h-5 text-ducens-muted" aria-hidden="true" />}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Button
          as="link"
          to="mailto:sebastian@ducens.io"
          external
          variant="primary"
          size="md"
        >
          <Mail className="w-4 h-4" />
          Écrire à Sebastian
        </Button>
        <Button
          as="link"
          to={ONBOARDING_LOOM_URL}
          external
          variant="outline"
          size="md"
        >
          <PlayCircle className="w-4 h-4" />
          Voir le Loom d'onboarding
        </Button>
      </div>
    </Card>
  );
}
